import React from 'react';
import { motion } from 'framer-motion';

const steps = [
  {
    number: "01",
    title: "Describe Your Device",
    description: "Explain how your device works, its intended purpose, and select your preferred data format (or provide your own). Specify the time frequency for data collection and generation.",
    icon: "📝",
    gradient: "from-primary to-primary-light",
    advantage: "Eliminate ETL complexity with custom data frame structure output"
  },
  {
    number: "02",
    title: "AI Case Analysis",
    description: "The system intelligently analyzes device requirements to understand anomalies, standard and non-standard cases to simulate, and prepares a comprehensive dataset tailored to specific project needs.",
    icon: "🧠",
    gradient: "from-secondary to-secondary-light",
    advantage: "Dramatically reduce your go-to-market time"
  },
  {
    number: "03",
    title: "Train Better Models",
    description: "Access your completed dataset via direct download link or through our automated S3-compatible interface, making it easy to integrate into your existing AI training pipeline.",
    icon: "🚀",
    gradient: "from-accent to-accent-light",
    advantage: "Receive your data in the required format automatically"
  }
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2
    }
  }
};

const item = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] } }
};

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="section relative overflow-hidden bg-background-secondary">
      {/* Background elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-primary/5 to-secondary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-gradient-to-r from-accent/5 to-primary/5 rounded-full blur-3xl"></div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
        >
          
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
            How It Works
          </h2>
          <p className="text-lg text-foreground-muted max-w-3xl mx-auto leading-relaxed">
            Create powerful synthetic datasets for IoT devices in three simple steps - based on your equipment profile and expected custom sensor outputs
          </p>
        </motion.div>
        
        {/* Steps in a responsive grid */}
        <motion.div 
          className="grid md:grid-cols-3 gap-8 relative"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
        >
          {/* Connection lines for desktop */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-border-light to-transparent transform -translate-y-1/2"></div>
          
          {steps.map((step, index) => (
            <motion.div 
              key={index}
              className="relative"
              variants={item}
            >
              {/* Connection dots */}
              <div className="hidden md:block absolute top-1/2 -right-4 w-6 h-6 bg-gradient-to-r from-primary to-accent rounded-full transform -translate-y-1/2 z-10 shadow-lg"></div>
              <div className="hidden md:block absolute top-1/2 -left-4 w-6 h-6 bg-gradient-to-r from-primary to-accent rounded-full transform -translate-y-1/2 z-10 shadow-lg"></div>
              
              <div className="card-elevated relative group overflow-hidden">
                {/* Purple header bar with step number */}
                <div className="bg-primary py-4 px-6 mb-6">
                  <div className="text-3xl font-bold step-number">
                    {step.number}
                  </div>
                </div>
                
                <div className="relative z-10 px-6 pb-6">
                  <h3 className="text-xl font-bold mb-4 text-foreground group-hover:text-primary transition-colors">
                    {step.title}
                  </h3>
                  
                  <p className="text-foreground-muted leading-relaxed mb-4">
                    {step.description}
                  </p>
                  
                  {/* Advantage Badge */}
                  <div className="mb-6">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent text-sm font-medium">
                      {step.advantage}
                    </div>
                  </div>
                  
                  {/* Progress indicator */}
                  <div className="h-1 bg-background rounded-full overflow-hidden">
                    <motion.div 
                      className={`h-full bg-gradient-to-r ${step.gradient} rounded-full`}
                      initial={{ width: 0 }}
                      whileInView={{ width: "100%" }}
                      viewport={{ once: true }}
                      transition={{ duration: 1, delay: index * 0.2 }}
                    ></motion.div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
        
      </div>
    </section>
  );
};

export default HowItWorks;
